import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Page } from '@/components/layout/page'
import { StaleDataHint } from '@/components/layout/offline-banner'
import { Skeleton } from '@/components/ui/skeleton'
import { api } from '@/api/http'
import { useIsOffline } from '@/hooks/use-offline'
import type { CSSProperties } from 'react'

export const Route = createFileRoute('/mrgolisoda/more/my-franchise/$franchiseId/')({
  validateSearch: (search: Record<string, unknown>) => ({
    name: typeof search.name === 'string' ? search.name : undefined,
  }),
  component: FranchiseDetailPage,
})

// ─── Date filter ──────────────────────────────────────────────────────────────

type DatePreset = 'today' | 'this_week' | 'last_week' | 'this_month' | 'last_month' | 'custom'

const PRESETS: { key: DatePreset; label: string }[] = [
  { key: 'today',      label: 'Today' },
  { key: 'this_week',  label: 'This week' },
  { key: 'last_week',  label: 'Last week' },
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'custom',     label: 'Custom' },
]

function displayDate(s: string): string {
  const [y, m, d] = s.split('-')
  return `${d}-${m}-${y}`
}

function fmt(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function getDateParams(
  preset: DatePreset,
  customStart: string,
  customEnd: string,
): Record<string, string> {
  const today = new Date()

  if (preset === 'today') return { date: fmt(today) }

  const mondayOf = (d: Date): Date => {
    const copy = new Date(d)
    const day = copy.getDay()
    copy.setDate(copy.getDate() - (day === 0 ? 6 : day - 1))
    copy.setHours(0, 0, 0, 0)
    return copy
  }

  if (preset === 'this_week') {
    return { start_date: fmt(mondayOf(today)), end_date: fmt(today) }
  }
  if (preset === 'last_week') {
    const thisMonday = mondayOf(today)
    const lastSunday = new Date(thisMonday)
    lastSunday.setDate(lastSunday.getDate() - 1)
    const lastMonday = mondayOf(lastSunday)
    return { start_date: fmt(lastMonday), end_date: fmt(lastSunday) }
  }
  if (preset === 'this_month') {
    const start = new Date(today.getFullYear(), today.getMonth(), 1)
    return { start_date: fmt(start), end_date: fmt(today) }
  }
  if (preset === 'last_month') {
    const start = new Date(today.getFullYear(), today.getMonth() - 1, 1)
    const end = new Date(today.getFullYear(), today.getMonth(), 0)
    return { start_date: fmt(start), end_date: fmt(end) }
  }
  if (preset === 'custom' && customStart && customEnd) {
    return { start_date: customStart, end_date: customEnd }
  }
  return { date: fmt(today) }
}

function dateLabel(preset: DatePreset, customStart: string, customEnd: string): string {
  const params = getDateParams(preset, customStart, customEnd)
  if (params.date) return displayDate(params.date)
  return `${displayDate(params.start_date!)} to ${displayDate(params.end_date!)}`
}

// ─── Outlet types ─────────────────────────────────────────────────────────────

interface OutletCategory {
  category: string
  sc: number
  tc: number
  pc: number
  target: number
  achieved: number
}

interface OutletFranchise {
  yolo_customer_id: string
  franchise: string
  categories: OutletCategory[]
}

interface OutletEmployee {
  employee_id: number
  employee_name: string
  has_subordinates: boolean
  franchises: OutletFranchise[]
}

interface OutletBDEResponse {
  role: 'BDE'
  date: string
  franchises: OutletFranchise[]
}

interface OutletManagerResponse {
  role: string
  date: string
  data: OutletEmployee[]
}

type OutletApiResponse = OutletBDEResponse | OutletManagerResponse

function getAllFranchisesForId(r: OutletApiResponse, franchiseId: string): OutletFranchise[] {
  const all = 'franchises' in r
    ? r.franchises
    : r.data.flatMap((emp) => emp.franchises)
  return all.filter((f) => f.yolo_customer_id === franchiseId)
}

const CATEGORY_ORDER = ['Visicooler', 'Buying', 'Non-Buying']

interface AggRow {
  category: string
  sc: number; tc: number; pc: number; target: number; achieved: number
}

function aggregateForFranchise(franchises: OutletFranchise[]): AggRow[] {
  const map = new Map<string, AggRow>()
  for (const f of franchises) {
    for (const c of f.categories) {
      const ex = map.get(c.category)
      if (ex) {
        ex.sc += c.sc; ex.tc += c.tc; ex.pc += c.pc
        ex.target += c.target; ex.achieved += c.achieved
      } else {
        map.set(c.category, { ...c })
      }
    }
  }
  return CATEGORY_ORDER.filter((cat) => map.has(cat)).map((cat) => map.get(cat)!)
}

// ─── Table styles ─────────────────────────────────────────────────────────────

const TH: CSSProperties = { padding: '4px 6px', fontWeight: 600, fontSize: 10, color: '#808080', borderBottom: '1px solid #E5E5E5' }
const TD: CSSProperties = { padding: '8px 6px', fontSize: 12, color: '#1A1A1A', fontVariantNumeric: 'tabular-nums', borderBottom: '1px solid #F0F0F0' }

function inr(n: number) {
  return '₹' + Math.abs(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })
}

// ─── Outlets table ────────────────────────────────────────────────────────────

function OutletsTable({ franchiseId, dateParams }: { franchiseId: string; dateParams: Record<string, string> }) {
  const qs = new URLSearchParams(dateParams).toString()

  const { data, isLoading } = useQuery({
    queryKey: ['outlet-targets-franchise', franchiseId, qs],
    queryFn: () => api.get<OutletApiResponse>(`/field-customization/goli-soda/outlet-targets/?${qs}`),
  })

  const rows = data ? aggregateForFranchise(getAllFranchisesForId(data, franchiseId)) : []
  const totals = rows.reduce(
    (a, r) => ({ sc: a.sc + r.sc, tc: a.tc + r.tc, pc: a.pc + r.pc, target: a.target + r.target, achieved: a.achieved + r.achieved }),
    { sc: 0, tc: 0, pc: 0, target: 0, achieved: 0 },
  )

  if (isLoading) return <Skeleton className="h-32 w-full" />

  if (rows.length === 0) return (
    <p style={{ fontSize: 12, color: '#808080', textAlign: 'center', padding: '16px 0' }}>No outlet data</p>
  )

  return (
    <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' as never }}>
      <table style={{ width: '100%', minWidth: 340, borderCollapse: 'collapse', tableLayout: 'auto' }}>
        <thead>
          <tr>
            <th style={{ ...TH, textAlign: 'left', whiteSpace: 'nowrap' }}>Outlet</th>
            <th style={{ ...TH, textAlign: 'right', whiteSpace: 'nowrap' }}>SC</th>
            <th style={{ ...TH, textAlign: 'right', whiteSpace: 'nowrap' }}>TC</th>
            <th style={{ ...TH, textAlign: 'right', whiteSpace: 'nowrap' }}>PC</th>
            <th style={{ ...TH, textAlign: 'right', whiteSpace: 'nowrap' }}>Target</th>
            <th style={{ ...TH, textAlign: 'right', whiteSpace: 'nowrap' }}>Done</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const diff = r.achieved - r.target
            return (
              <tr key={r.category}>
                <td style={{ ...TD, color: '#104BFC', fontWeight: 600, textAlign: 'left', whiteSpace: 'nowrap' }}>{r.category}</td>
                <td style={{ ...TD, textAlign: 'right', whiteSpace: 'nowrap' }}>{r.sc}</td>
                <td style={{ ...TD, textAlign: 'right', whiteSpace: 'nowrap' }}>{r.tc}</td>
                <td style={{ ...TD, textAlign: 'right', whiteSpace: 'nowrap' }}>{r.pc}</td>
                <td style={{ ...TD, textAlign: 'right', whiteSpace: 'nowrap' }}>{r.target > 0 ? inr(r.target) : '—'}</td>
                <td style={{ ...TD, textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <div>{r.achieved > 0 ? inr(r.achieved) : '—'}</div>
                  {r.target > 0 && (
                    <div style={{ fontSize: 10, fontWeight: 600, color: diff < 0 ? '#E51414' : '#226600' }}>
                      {diff < 0 ? '−' : '+'}{Math.abs(diff).toLocaleString('en-IN')}
                    </div>
                  )}
                </td>
              </tr>
            )
          })}
          <tr style={{ fontWeight: 800 }}>
            <td style={{ ...TD, textAlign: 'left', borderBottom: 'none', borderTop: '1.5px solid #E5E5E5', fontSize: 13, whiteSpace: 'nowrap' }}>Total</td>
            <td style={{ ...TD, textAlign: 'right', borderBottom: 'none', borderTop: '1.5px solid #E5E5E5', whiteSpace: 'nowrap' }}>{totals.sc}</td>
            <td style={{ ...TD, textAlign: 'right', borderBottom: 'none', borderTop: '1.5px solid #E5E5E5', whiteSpace: 'nowrap' }}>{totals.tc}</td>
            <td style={{ ...TD, textAlign: 'right', borderBottom: 'none', borderTop: '1.5px solid #E5E5E5', whiteSpace: 'nowrap' }}>{totals.pc}</td>
            <td style={{ ...TD, textAlign: 'right', borderBottom: 'none', borderTop: '1.5px solid #E5E5E5', whiteSpace: 'nowrap' }}>{totals.target > 0 ? inr(totals.target) : '—'}</td>
            <td style={{ ...TD, textAlign: 'right', borderBottom: 'none', borderTop: '1.5px solid #E5E5E5', whiteSpace: 'nowrap' }}>
              <div>{totals.achieved > 0 ? inr(totals.achieved) : '—'}</div>
              {totals.target > 0 && (
                <div style={{ fontSize: 10, fontWeight: 600, color: totals.achieved - totals.target < 0 ? '#E51414' : '#226600' }}>
                  {totals.achieved - totals.target < 0 ? '−' : '+'}{Math.abs(totals.achieved - totals.target).toLocaleString('en-IN')}
                </div>
              )}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

// ─── Routes section ───────────────────────────────────────────────────────────

interface RouteRow {
  route_id: number
  route_name: string
  sc: number
  tc: number
  pc: number
  achieved: number
}

interface RouteSummaryResponse {
  role: string
  date: string
  franchise_id: number
  routes: RouteRow[]
}

function RoutesSection({ franchiseId }: { franchiseId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['franchise-routes', franchiseId],
    queryFn: () => api.get<RouteSummaryResponse>(
      `/field-customization/goli-soda/franchise-route-summary/?franchise_id=${franchiseId}`,
    ),
  })

  if (isLoading) return <Skeleton className="h-24 w-full" />

  const routes = data?.routes ?? []

  if (routes.length === 0) {
    return <p style={{ fontSize: 12, color: '#808080', textAlign: 'center', padding: '12px 0' }}>No routes today</p>
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {routes.map((r, i) => (
        <div
          key={r.route_id}
          style={{
            padding: '11px 0',
            borderBottom: i < routes.length - 1 ? '1px solid #F0F0F0' : 'none',
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 700, color: '#1A1A1A', marginBottom: 3, lineHeight: 1.4, wordBreak: 'break-word' }}>
            {r.route_name}
          </div>
          <div style={{ fontSize: 12, color: '#666666' }}>
            SC {r.sc} · TC {r.tc} · PC {r.pc}
            {r.achieved > 0 ? ` · ${inr(r.achieved)}` : ''}
          </div>
        </div>
      ))}
    </div>
  )
}

// ─── Franchise report (non-today) ─────────────────────────────────────────────

interface ReportCategory {
  category: string
  sc: number
  tc: number
  pc: number
  target: string
  achieved: string
}

interface ReportRoute {
  route_id: number
  route_name: string
  sc: number
  tc: number
  pc: number
  target: string
  achieved: string
  categories: ReportCategory[]
}

interface FranchiseReportResponse {
  franchise_id: number
  from_date: string
  to_date: string
  routes: ReportRoute[]
  overall: ReportCategory[]
}

function ReportOverallTable({ rows }: { rows: ReportCategory[] }) {
  const dataRows = rows.filter((r) => r.category !== 'Total')
  const totalRow = rows.find((r) => r.category === 'Total')

  const renderRow = (r: ReportCategory, isTotal: boolean) => {
    const target = parseFloat(r.target)
    const achieved = parseFloat(r.achieved)
    const diff = achieved - target
    const borderTop = isTotal ? '1.5px solid #E5E5E5' : undefined
    const borderBottom = isTotal ? 'none' : undefined
    return (
      <tr key={r.category} style={isTotal ? { fontWeight: 800 } : undefined}>
        <td style={{ ...TD, textAlign: 'left', borderTop, borderBottom, whiteSpace: 'nowrap' }}>{r.category}</td>
        <td style={{ ...TD, textAlign: 'right', borderTop, borderBottom, whiteSpace: 'nowrap' }}>{r.sc}</td>
        <td style={{ ...TD, textAlign: 'right', borderTop, borderBottom, whiteSpace: 'nowrap' }}>{r.tc}</td>
        <td style={{ ...TD, textAlign: 'right', borderTop, borderBottom, whiteSpace: 'nowrap' }}>{r.pc}</td>
        <td style={{ ...TD, textAlign: 'right', borderTop, borderBottom, whiteSpace: 'nowrap' }}>{target > 0 ? inr(target) : '—'}</td>
        <td style={{ ...TD, textAlign: 'right', borderTop, borderBottom, whiteSpace: 'nowrap' }}>
          <div>{achieved > 0 ? inr(achieved) : '—'}</div>
          {target > 0 && !isTotal && (
            <div style={{ fontSize: 10, fontWeight: 600, color: diff < 0 ? '#E51414' : '#226600' }}>
              {diff < 0 ? '−' : '+'}{inr(Math.abs(diff)).slice(1)}
            </div>
          )}
        </td>
      </tr>
    )
  }

  return (
    <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' as never }}>
      <table style={{ width: '100%', minWidth: 340, borderCollapse: 'collapse', tableLayout: 'auto' }}>
        <thead>
          <tr>
            <th style={{ ...TH, textAlign: 'left', whiteSpace: 'nowrap' }}>Outlet</th>
            <th style={{ ...TH, textAlign: 'right', whiteSpace: 'nowrap' }}>SC</th>
            <th style={{ ...TH, textAlign: 'right', whiteSpace: 'nowrap' }}>TC</th>
            <th style={{ ...TH, textAlign: 'right', whiteSpace: 'nowrap' }}>PC</th>
            <th style={{ ...TH, textAlign: 'right', whiteSpace: 'nowrap' }}>Target</th>
            <th style={{ ...TH, textAlign: 'right', whiteSpace: 'nowrap' }}>Done</th>
          </tr>
        </thead>
        <tbody>
          {dataRows.map((r) => renderRow(r, false))}
          {totalRow && renderRow(totalRow, true)}
        </tbody>
      </table>
    </div>
  )
}

function ReportRoutesList({ routes }: { routes: ReportRoute[] }) {
  if (routes.length === 0) {
    return <p style={{ fontSize: 12, color: '#808080', textAlign: 'center', padding: '12px 0' }}>No routes</p>
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {routes.map((r, i) => {
        const achieved = parseFloat(r.achieved)
        const target = parseFloat(r.target)
        return (
          <div
            key={r.route_id}
            style={{ padding: '11px 0', borderBottom: i < routes.length - 1 ? '1px solid #F0F0F0' : 'none' }}
          >
            <div style={{ fontSize: 13, fontWeight: 700, color: '#1A1A1A', marginBottom: 3, lineHeight: 1.4, wordBreak: 'break-word' }}>{r.route_name}</div>
            <div style={{ fontSize: 12, color: '#666666' }}>
              SC {r.sc} · TC {r.tc} · PC {r.pc}
              {target > 0 ? ` · ${inr(achieved)} / ${inr(target)}` : ''}
            </div>
          </div>
        )
      })}
    </div>
  )
}

function FranchiseReportSections({
  franchiseId,
  fromDate,
  toDate,
  label,
}: {
  franchiseId: string
  fromDate: string
  toDate: string
  label: string
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['franchise-report', franchiseId, fromDate, toDate],
    queryFn: () =>
      api.get<FranchiseReportResponse>(
        `/field-customization/goli-soda/franchise-report/?franchise_id=${franchiseId}&from_date=${fromDate}&to_date=${toDate}`,
      ),
  })

  if (isLoading) {
    return (
      <>
        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #EBEBEB', padding: 14 }}>
          <Skeleton className="h-32 w-full" />
        </div>
        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #EBEBEB', padding: 14 }}>
          <Skeleton className="h-24 w-full" />
        </div>
      </>
    )
  }

  const overall = data?.overall ?? []
  const routes = data?.routes ?? []

  return (
    <>
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #EBEBEB', padding: 14 }}>
        <p style={{ fontSize: 13, fontWeight: 700, color: '#1A1A1A', marginBottom: 10 }}>
          Outlets and calls
          <span style={{ fontSize: 11, fontWeight: 400, color: '#808080', marginLeft: 6 }}>{label}</span>
        </p>
        {overall.length > 0
          ? <ReportOverallTable rows={overall} />
          : <p style={{ fontSize: 12, color: '#808080', textAlign: 'center', padding: '12px 0' }}>No outlet data</p>
        }
      </div>
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #EBEBEB', padding: 14 }}>
        <p style={{ fontSize: 13, fontWeight: 700, color: '#1A1A1A', marginBottom: 10 }}>
          Routes
          <span style={{ fontSize: 11, fontWeight: 400, color: '#808080', marginLeft: 6 }}>{label}</span>
        </p>
        <ReportRoutesList routes={routes} />
      </div>
    </>
  )
}

// ─── Date filter UI ───────────────────────────────────────────────────────────

const INPUT_STYLE: CSSProperties = {
  border: '1px solid #E5E5E5',
  borderRadius: 8,
  padding: '7px 10px',
  fontSize: 13,
  color: '#1A1A1A',
  background: '#fff',
  flex: 1,
  outline: 'none',
  fontFamily: 'inherit',
  minWidth: 0,
}

function DateFilter({
  preset,
  setPreset,
  customStart,
  setCustomStart,
  customEnd,
  setCustomEnd,
  onApply,
}: {
  preset: DatePreset
  setPreset: (p: DatePreset) => void
  customStart: string
  setCustomStart: (s: string) => void
  customEnd: string
  setCustomEnd: (s: string) => void
  onApply: () => void
}) {
  const applyReady = !!customStart && !!customEnd && customStart <= customEnd

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4 }}>
        {PRESETS.map((p) => (
          <button
            key={p.key}
            onClick={() => setPreset(p.key)}
            style={{
              flexShrink: 0,
              padding: '6px 12px',
              borderRadius: 20,
              border: 'none',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
              background: preset === p.key ? '#1A1A1A' : '#F0F0F0',
              color: preset === p.key ? '#fff' : '#666666',
            }}
          >
            {p.label}
          </button>
        ))}
      </div>

      {preset === 'custom' && (
        <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' }}>
          <input
            type="date"
            value={customStart}
            onChange={(e) => setCustomStart(e.target.value)}
            style={INPUT_STYLE}
          />
          <span style={{ color: '#808080', fontSize: 12, flexShrink: 0 }}>to</span>
          <input
            type="date"
            value={customEnd}
            min={customStart}
            onChange={(e) => setCustomEnd(e.target.value)}
            style={INPUT_STYLE}
          />
          <button
            onClick={onApply}
            disabled={!applyReady}
            style={{
              flexShrink: 0,
              padding: '7px 14px',
              borderRadius: 8,
              border: 'none',
              fontSize: 13,
              fontWeight: 700,
              cursor: applyReady ? 'pointer' : 'not-allowed',
              fontFamily: 'inherit',
              background: applyReady ? '#104BFC' : '#E0E0E0',
              color: applyReady ? '#fff' : '#9E9E9E',
            }}
          >
            Apply
          </button>
        </div>
      )}
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

function FranchiseDetailPage() {
  const { franchiseId } = Route.useParams()
  const { name: franchiseName = 'Franchise' } = Route.useSearch()
  const offline = useIsOffline()

  const todayFmt = fmt(new Date())
  const [preset, setPreset] = useState<DatePreset>('today')
  const [customStart, setCustomStart] = useState(todayFmt)
  const [customEnd, setCustomEnd] = useState(todayFmt)
  const [appliedRange, setAppliedRange] = useState<{ start: string; end: string } | null>(null)

  function handleSetPreset(p: DatePreset) {
    if (p !== 'custom') setAppliedRange(null)
    setPreset(p)
  }

  function handleApply() {
    if (customStart && customEnd && customStart <= customEnd) {
      setAppliedRange({ start: customStart, end: customEnd })
    }
  }

  const dateParams = getDateParams(preset, customStart, customEnd)
  const rangeReady = preset !== 'custom' || !!appliedRange

  const sectionLabel = preset === 'custom' && appliedRange
    ? `${displayDate(appliedRange.start)} to ${displayDate(appliedRange.end)}`
    : dateLabel(preset, customStart, customEnd)

  return (
    <Page title={franchiseName} back scaled>
      {offline && <StaleDataHint />}
      <div style={{ padding: '16px 16px 40px', background: '#F5F5F5', minHeight: '100%', display: 'flex', flexDirection: 'column', gap: 12 }}>

        {/* Date filter */}
        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #EBEBEB', padding: '14px 14px 12px' }}>
          <DateFilter
            preset={preset}
            setPreset={handleSetPreset}
            customStart={customStart}
            setCustomStart={setCustomStart}
            customEnd={customEnd}
            setCustomEnd={setCustomEnd}
            onApply={handleApply}
          />
        </div>

        {preset === 'today' ? (
          <>
            {/* Today: outlet-targets API + franchise-route-summary API */}
            <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #EBEBEB', padding: 14 }}>
              <p style={{ fontSize: 13, fontWeight: 700, color: '#1A1A1A', marginBottom: 10 }}>
                Outlets and calls
                <span style={{ fontSize: 11, fontWeight: 400, color: '#808080', marginLeft: 6 }}>{sectionLabel}</span>
              </p>
              <OutletsTable franchiseId={franchiseId} dateParams={dateParams} />
            </div>
            <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #EBEBEB', padding: 14 }}>
              <p style={{ fontSize: 13, fontWeight: 700, color: '#1A1A1A', marginBottom: 10 }}>
                Routes
                <span style={{ fontSize: 11, fontWeight: 400, color: '#808080', marginLeft: 6 }}>{sectionLabel}</span>
              </p>
              <RoutesSection franchiseId={franchiseId} />
            </div>
          </>
        ) : rangeReady ? (
          /* Non-today: franchise-report API drives both sections */
          <FranchiseReportSections
            franchiseId={franchiseId}
            fromDate={preset === 'custom' ? appliedRange!.start : dateParams.start_date!}
            toDate={preset === 'custom' ? appliedRange!.end : dateParams.end_date!}
            label={sectionLabel}
          />
        ) : (
          <p style={{ fontSize: 12, color: '#808080', padding: '8px 0', textAlign: 'center' }}>Select a valid date range</p>
        )}

      </div>
    </Page>
  )
}
